/**
 * Presidio — Azure AD identity provider.
 * Logout returns to Sign-in (`/login`) instead of `/home`.
 */
import { APP_BASE_HREF } from '@angular/common';
import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, ReplaySubject, combineLatest, from, of, race, timer } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, tap } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { UserData } from 'ish-core/models/user/user.interface';
import { ApiService } from 'ish-core/services/api/api.service';
import { getSsoRegistrationCancelled, getSsoRegistrationRegistered } from 'ish-core/store/customer/sso-registration';
import {
  getLoggedInCustomer,
  getUserAuthorized,
  getUserLoading,
  loadUserByAPIToken,
} from 'ish-core/store/customer/user';
import { ApiTokenService } from 'ish-core/utils/api-token/api-token.service';
import { InstanceCreators } from 'ish-core/utils/instance-creators';
import { whenTruthy } from 'ish-core/utils/operators';

import { IdentityProvider, IdentityProviderCapabilities, TriggerReturnType } from './identity-provider.interface';

export interface AzureConfig {
  type: 'azure';
  issuer: string; // e.g. 'https://login.microsoftonline.com/common/v2.0'
  clientID: string;
}

function getTenantFromIssuer(issuer: string): string {
  const matches = issuer.match(/login\.microsoftonline\.com\/([^/]+)\/v2\.0/);
  return matches ? matches[1] : 'common';
}

function decodeState(state: string): string {
  if (!state) {
    return '/home';
  }
  try {
    let decoded = state;
    let prev: string | null = null;
    while (decoded !== prev) {
      prev = decoded;
      decoded = decodeURIComponent(decoded);
    }
    const parts = decoded.split(';');
    const route = parts.pop() || '/home';
    if (!route || route === '/' || route.startsWith('/login') || route.startsWith('/logout') || route.startsWith('/error')) {
      return '/home';
    }
    return route.startsWith('/') ? route : `/${route}`;
  } catch {
    return '/home';
  }
}

/** Post-login target when returning from Azure on the Sign-in page. */
function postLoginTargetFromLoginUrl(currentUrl: string): string {
  try {
    const qIndex = currentUrl.indexOf('?');
    if (qIndex < 0) {
      return '/home';
    }
    const params = new URLSearchParams(currentUrl.slice(qIndex + 1));
    const returnUrl = (params.get('returnUrl') || '').trim();
    if (!returnUrl || returnUrl === '/' || returnUrl.startsWith('/login') || returnUrl.startsWith('/logout') || returnUrl.startsWith('/error')) {
      return '/home';
    }
    return returnUrl.startsWith('/') ? returnUrl : `/${returnUrl}`;
  } catch {
    return '/home';
  }
}

@Injectable({ providedIn: 'root' })
export class AzureIdentityProvider implements IdentityProvider {
  private oauthService: OAuthService;
  private discoveryReadySubject = new ReplaySubject<boolean>(1);
  discoveryReady$ = this.discoveryReadySubject.asObservable();

  constructor(
    private apiService: ApiService,
    private store: Store,
    private router: Router,
    private apiTokenService: ApiTokenService,
    private accountFacade: AccountFacade,
    parent: Injector,
    @Inject(APP_BASE_HREF) private baseHref: string
  ) {
    this.oauthService = InstanceCreators.getOAuthServiceInstance(parent);
  }

  getCapabilities(): IdentityProviderCapabilities {
    return {
      editPassword: false,
      editEmail: false,
      editProfile: false,
    };
  }

  init(config: AzureConfig) {
    if (SSR) {
      console.log('AzureIdentityProvider: Skipping init() during SSR');
      return;
    }
    const effectiveOrigin = this.baseHref === '/' ? window.location.origin : window.location.origin + this.baseHref;
    const tenant = getTenantFromIssuer(config.issuer);
    const silentRefresh = `${window.location.origin}/assets/silent-refresh.html`;
    this.oauthService.configure({
      issuer: config.issuer,
      clientId: config.clientID,
      postLogoutRedirectUri: effectiveOrigin,
      scope: 'openid profile email',
      responseType: 'code',
      sessionChecksEnabled: false,
      silentRefreshRedirectUri: silentRefresh,
      strictDiscoveryDocumentValidation: false,
      showDebugInformation: true,

      logoutUrl: `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/logout`,
    });
    this.oauthService.loadDiscoveryDocument().then(() => {
      this.discoveryReadySubject.next(true);
    });
    this.oauthService.setupAutomaticSilentRefresh();

    this.apiTokenService
      .restore$(['user', 'order'])
      .pipe(
        switchMap(() => from(this.oauthService.loadDiscoveryDocumentAndTryLogin())),
        switchMap(() =>
          timer(0, 200).pipe(
            map(() => this.oauthService.getIdToken()),
            whenTruthy(),
            take(1)
          )
        ),
        switchMap(idToken => {
          if (!idToken) {
            return of(null);
          }
          // As soon as we have a token, do the immediate redirect handling
          this.handleImmediateRedirect();
          const inviteUserId = sessionStorage.getItem('invite-userid');
          const inviteHash = sessionStorage.getItem('invite-hash');
          return inviteUserId && inviteHash
            ? this.inviteRegistration(idToken, inviteUserId, inviteHash).pipe(
                tap(() => {
                  sessionStorage.removeItem('invite-userid');
                  sessionStorage.removeItem('invite-hash');
                })
              )
            : this.normalSignInRegistration(idToken);
        })
      )
      .subscribe(() => {
        this.apiTokenService.removeApiToken();
        localStorage.setItem('activeIdentityProvider', 'Azure');
      });
  }

  private handleImmediateRedirect() {
    const idToken = this.oauthService.getIdToken();
    if (!idToken) {
      return;
    }
    // Kick off background user load
    this.store.dispatch(loadUserByAPIToken({}));
  }

  private normalSignInRegistration(idToken: string) {
    return this.apiService.post<UserData>('users/processtoken', { id_token: idToken, options: ['CREATE_USER'] }).pipe(
      switchMap((userData: UserData) =>
        combineLatest([this.store.pipe(select(getLoggedInCustomer)), this.store.pipe(select(getUserLoading))]).pipe(
          filter(([, loading]) => !loading),
          first(),
          switchMap(([customer]) =>
            !customer
              ? this.router.navigate(['/register', 'sso'], {
                  queryParams: {
                    userid: userData.businessPartnerNo,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                  },
                })
              : of(false)
          ),
          switchMap(navigated =>
            navigated || navigated === null
              ? race(
                  this.store.pipe(
                    select(getSsoRegistrationRegistered),
                    whenTruthy(),
                    tap(() => this.store.dispatch(loadUserByAPIToken({})))
                  ),
                  this.store.pipe(
                    select(getSsoRegistrationCancelled),
                    whenTruthy(),
                    tap(() => this.router.navigateByUrl('/logout'))
                  )
                )
              : of(navigated)
          )
        )
      ),
      switchMap(() => this.store.pipe(select(getUserAuthorized), whenTruthy(), first())),
      catchError((error: HttpError) => {
        this.apiTokenService.removeApiToken();
        this.triggerLogout();
        return of(error);
      })
    );
  }

  private inviteRegistration(idToken: string, userId: string, hash: string) {
    return this.apiService
      .post<UserData>('users/processtoken', {
        id_token: idToken,
        secure_user_ref: {
          user_id: userId,
          secure_code: hash,
        },
        options: ['UPDATE'],
      })
      .pipe(
        tap(() => this.store.dispatch(loadUserByAPIToken({}))),
        switchMap(() => this.store.pipe(select(getUserAuthorized), whenTruthy(), first())),
        catchError((error: HttpError) => {
          this.apiTokenService.removeApiToken();
          this.triggerLogout();
          return of(error);
        })
      );
  }

  triggerRegister(route: ActivatedRouteSnapshot): TriggerReturnType {
    if (route.queryParamMap.get('userid')) {
      return of(true);
    }
    return this.oauthService.loadDiscoveryDocumentAndLogin({ state: route.queryParams.returnUrl });
  }

  triggerLogin(): TriggerReturnType {
    if (this.oauthService.hasValidIdToken()) {
      return of(true);
    }
    return this.discoveryReady$.pipe(
      take(1),
      switchMap(() => {
        this.oauthService.customQueryParams = {
          prompt: this.oauthService.hasValidAccessToken() ? 'none' : 'select_account',
        };

        return from(this.oauthService.initLoginFlowInPopup({ width: 500, height: 700 }));
      }),
      switchMap(() =>
        timer(0, 100).pipe(
          map(() => this.oauthService.getIdToken()),
          whenTruthy(),
          take(1)
        )
      ),
      tap(() => this.afterLogin()),
      map(() => false)
    );
  }

  private afterLogin(): void {
    if (!this.oauthService.hasValidIdToken()) {
      return;
    }
    this.store.dispatch(loadUserByAPIToken({}));

    this.store.pipe(select(getUserAuthorized), whenTruthy(), first()).subscribe(() => {
      const currentUrl = this.router.url;
      if (currentUrl === '/login' || currentUrl.startsWith('/login?')) {
        const target = postLoginTargetFromLoginUrl(currentUrl);
        setTimeout(() => {
          this.router.navigateByUrl(target);
        }, 0);
        return;
      }
      if (currentUrl === '/multiple-shipping-checkout' || currentUrl.startsWith('/multiple-shipping-checkout?')) {
        setTimeout(() => {
          this.router.navigateByUrl(currentUrl);
        }, 0);
        return;
      }

      const target = decodeState(currentUrl);
      this.router.navigateByUrl(target);
      this.discoveryReadySubject.next(false);
      this.discoveryReadySubject.complete();
    });
  }

  triggerLogout(): TriggerReturnType {
    localStorage.removeItem('activeIdentityProvider');
    this.apiTokenService.removeApiToken();
    this.accountFacade.logoutUser();

    try {
      if (this.oauthService.hasValidIdToken()) {
        this.oauthService.logOut(true);
      }
    } catch (error) {
      console.error('Azure logout error:', error);
    }

    this.router.navigateByUrl('/login?forcePageView=true');
    return of(this.router.parseUrl('/login?forcePageView=true'));
  }
  triggerInvite(route: ActivatedRouteSnapshot): TriggerReturnType {
    sessionStorage.setItem('invite-userid', route.queryParams.uid);
    sessionStorage.setItem('invite-hash', route.queryParams.Hash);
    return this.oauthService.loadDiscoveryDocumentAndLogin({
      state: route.queryParams.returnUrl,
    });
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.oauthService.getIdToken()) {
      return this.apiTokenService.intercept(req, next);
    }

    const newRequest =
      !req.url.endsWith('users/processtoken') && !req.headers.has(ApiService.TOKEN_HEADER_KEY)
        ? req.clone({
            headers: req.headers.set(ApiService.AUTHORIZATION_HEADER_KEY, `Bearer ${this.oauthService.getIdToken()}`),
          })
        : req;

    return next.handle(newRequest);
  }
}
